import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Wifi } from "lucide-react";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-[#0A0A0A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#34C759] to-[#28a745] rounded-xl flex items-center justify-center">
              <Wifi className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">AVX RF PRO</h1>
              <p className="text-[9px] text-[#34C759] font-medium tracking-widest">POLÍTICA DE PRIVACIDAD</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#34C759]/20 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#34C759]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Política de Privacidad</h1>
            <p className="text-gray-400">Última actualización: Marzo 2026</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">1. Información que Recopilamos</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Recopilamos información que usted nos proporciona directamente:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong className="text-white">Datos de cuenta:</strong> Nombre, email, contraseña encriptada</li>
              <li><strong className="text-white">Datos de uso:</strong> Configuraciones de sistemas IEM, frecuencias, eventos</li>
              <li><strong className="text-white">Datos de pago:</strong> Procesados de forma segura por Flow.cl (no almacenamos tarjetas)</li>
              <li><strong className="text-white">Datos técnicos:</strong> Tipo de navegador, dirección IP, dispositivo</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">2. Cómo Usamos su Información</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Utilizamos la información recopilada para:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Proporcionar, mantener y mejorar nuestros servicios</li>
              <li>Procesar transacciones y enviar notificaciones relacionadas</li>
              <li>Enviar actualizaciones técnicas y alertas de seguridad</li>
              <li>Responder a sus comentarios y preguntas</li>
              <li>Analizar tendencias de uso para mejorar la experiencia</li>
              <li>Prevenir actividades fraudulentas</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">3. Almacenamiento de Datos</h2>
            <p className="text-gray-300 leading-relaxed">
              Sus datos se almacenan de forma segura en servidores protegidos. Implementamos medidas de seguridad 
              incluyendo:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mt-4">
              <li>Encriptación de contraseñas con bcrypt</li>
              <li>Conexiones HTTPS/SSL</li>
              <li>Autenticación JWT con tokens seguros</li>
              <li>Acceso restringido a datos sensibles</li>
              <li>Backups regulares encriptados</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">4. Compartir Información</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">No vendemos sus datos personales.</strong> Solo compartimos información con:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong className="text-white">Flow.cl:</strong> Para procesamiento de pagos</li>
              <li><strong className="text-white">Proveedores de servicios:</strong> Que nos ayudan a operar la plataforma</li>
              <li><strong className="text-white">Autoridades legales:</strong> Cuando sea requerido por ley</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">5. Sus Derechos</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Usted tiene derecho a:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong className="text-white">Acceso:</strong> Solicitar una copia de sus datos personales</li>
              <li><strong className="text-white">Rectificación:</strong> Corregir datos inexactos</li>
              <li><strong className="text-white">Eliminación:</strong> Solicitar la eliminación de su cuenta y datos</li>
              <li><strong className="text-white">Portabilidad:</strong> Exportar sus datos en formato estándar</li>
              <li><strong className="text-white">Oposición:</strong> Oponerse al procesamiento de sus datos</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Para ejercer estos derechos, contáctenos a través de los canales indicados al final de esta política.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">6. Cookies y Tecnologías Similares</h2>
            <p className="text-gray-300 leading-relaxed">
              Utilizamos cookies esenciales para el funcionamiento de la plataforma:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mt-4">
              <li><strong className="text-white">Cookies de sesión:</strong> Para mantener su sesión iniciada</li>
              <li><strong className="text-white">Cookies de preferencias:</strong> Para recordar su configuración</li>
              <li><strong className="text-white">LocalStorage:</strong> Para funcionalidad offline</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">7. Retención de Datos</h2>
            <p className="text-gray-300 leading-relaxed">
              Conservamos sus datos mientras su cuenta esté activa o según sea necesario para proporcionarle 
              servicios. Puede solicitar la eliminación de su cuenta en cualquier momento. Algunos datos pueden 
              conservarse por requisitos legales o de facturación.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">8. Menores de Edad</h2>
            <p className="text-gray-300 leading-relaxed">
              AVX RF PRO es un servicio profesional destinado a usuarios mayores de 18 años. No recopilamos 
              intencionalmente información de menores de edad.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">9. Cambios a esta Política</h2>
            <p className="text-gray-300 leading-relaxed">
              Podemos actualizar esta política ocasionalmente. Le notificaremos sobre cambios significativos 
              por email o mediante un aviso en la plataforma. Le recomendamos revisar esta política periódicamente.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">10. Contacto</h2>
            <p className="text-gray-300 leading-relaxed">
              Para consultas sobre privacidad o ejercer sus derechos, contáctenos:
            </p>
            <div className="mt-4 p-4 bg-[#0A0A0A] rounded-lg">
              <p className="text-white font-medium">Anleon Chile - Protección de Datos</p>
              <p className="text-gray-400">Email: privacidad@avxrfpro.com</p>
              <p className="text-gray-400">Santiago, Chile</p>
            </div>
          </section>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 pt-8 border-t border-white/[0.08]">
            <div className="flex items-center gap-2 text-gray-400">
              <Shield className="w-5 h-5 text-[#34C759]" />
              <span>Datos Encriptados</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Shield className="w-5 h-5 text-[#34C759]" />
              <span>HTTPS Seguro</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Shield className="w-5 h-5 text-[#34C759]" />
              <span>No Vendemos Datos</span>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate("/")}
            className="bg-[#34C759] hover:bg-[#2db54d] text-black font-semibold px-8"
          >
            Volver al Inicio
          </Button>
        </div>
      </main>
    </div>
  );
}
